import { useState, useEffect, useRef } from "react";
import Select, { components } from "react-select";
import { FormControl, FormLabel, FormErrorMessage, useToast, Input } from "@chakra-ui/react";
import { User } from "@/models/user.model";
import { getUsersByCorbanId } from "@/services/user.service";
import { cpfFormatter } from "@/utils/cpfFormatter";

interface CertifiedCpfSelectorProps {
  corbanId?: number;
  value?: string;
  onChange: (cpf: string) => void;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  error?: string;
  isDisabled?: boolean;
}

interface UserOption {
  value: string;
  label: string;
  document: string;
}

export default function CertifiedCpfSelector({
  corbanId,
  value,
  onChange,
  label = "CPF do Vendedor Autorizado",
  placeholder = "Digite para pesquisar por nome ou CPF...",
  isRequired = false,
  error,
  isDisabled = false,
}: CertifiedCpfSelectorProps) {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState<UserOption | null>(null);
  const toast = useToast();
  const isLoadingMore = useRef(false);
  const scrollPositionRef = useRef(0);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const requestedCpfRef = useRef<string | null>(null);

  useEffect(() => {
    if (corbanId) {
      setUsers([]);
      setPage(1);
      setHasMore(true);
      setSearchTerm("");
      requestedCpfRef.current = null;
      if (value) {
        requestedCpfRef.current = value;
        loadInitialValue(value);
      } else {
        fetchUsers(1, "");
      }
    }
  }, [corbanId]);

  useEffect(() => {
    if (!value) {
      requestedCpfRef.current = null;
      setSelectedOption(null);
      return;
    }

    const selected = users.find(u => u.value === value);
    if (selected) {
      setSelectedOption(selected);
      return;
    }

    if (corbanId && requestedCpfRef.current !== value) {
      requestedCpfRef.current = value;
      loadInitialValue(value);
    }
  }, [value, users, corbanId]);

  const loadInitialValue = async (cpf: string) => {
    if (!corbanId || !cpf) return;
    
    try {
      console.log(`Carregando valor inicial para CPF: ${cpf}`);
      const responseWithCpf = await getUsersByCorbanId(
        corbanId,
        1,
        10,
        "name",
        "asc",
        cpf.replace(/\D/g, "")
      );

      let selectedUserOption: UserOption | null = null;

      if (responseWithCpf.data.length > 0) {
        const user = responseWithCpf.data.find((u: User) => 
          u.document?.replace(/\D/g, "") === cpf
        ) || responseWithCpf.data[0];
        
        selectedUserOption = {
          value: user.document?.replace(/\D/g, "") || "",
          label: `${user.name} ${user.lastName} - CPF: ${formatCPF(
            user.document || ""
          )}`,
          document: user.document || "",
        };
        
        setSelectedOption(selectedUserOption);
      }

      // Carrega lista completa para permitir mudança
      const responseAll = await getUsersByCorbanId(
        corbanId,
        1,
        10,
        "name",
        "asc",
        ""
      );

      const allOptions: UserOption[] = responseAll.data.map((user: User) => ({
        value: user.document?.replace(/\D/g, "") || "",
        label: `${user.name} ${user.lastName} - CPF: ${formatCPF(
          user.document || ""
        )}`,
        document: user.document || "",
      }));

      // Se o usuário selecionado não está na lista, adiciona no início
      if (selectedUserOption) {
        const exists = allOptions.some(u => u.value === selectedUserOption!.value);
        if (!exists) {
          setUsers([selectedUserOption, ...allOptions]);
        } else {
          setUsers(allOptions);
        }
      } else {
        setUsers(allOptions);
      }

      setHasMore(1 < responseAll.totalPages);
    } catch (error) {
      console.error("Erro ao carregar valor inicial", error);
      // Em caso de erro, carrega lista normal
      fetchUsers(1, "");
    }
  };

  const fetchUsers = async (currentPage: number, search: string) => {
    if (isLoadingMore.current || !corbanId) return;
    
    isLoadingMore.current = true;
    setIsLoading(true);
    
    try {
      console.log(`Buscando usuários - Página: ${currentPage}, Pesquisa: "${search}"`);
      const response = await getUsersByCorbanId(
        corbanId,
        currentPage,
        10,
        "name",
        "asc",
        search
      );

      const newOptions: UserOption[] = response.data.map((user: User) => ({
        value: user.document?.replace(/\D/g, "") || "",
        label: `${user.name} ${user.lastName} - CPF: ${formatCPF(
          user.document || ""
        )}`,
        document: user.document || "",
      }));

      if (currentPage === 1) {
        setUsers(newOptions);
      } else {
        setUsers((prev) => {
          const updated = [...prev, ...newOptions];
          return updated;
        });
      }

      setHasMore(currentPage < response.totalPages);
    } catch (error: any) {
      if (error?.status !== 404) {
        toast({
          title: "Erro ao buscar usuários",
          description: "Não foi possível carregar a lista de usuários.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
      isLoadingMore.current = false;
    }
  };

  const handleInputChange = (inputValue: string) => {
    setSearchTerm(inputValue);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    searchTimeout.current = setTimeout(() => {
      setPage(1);
      setUsers([]);
      fetchUsers(1, inputValue);
    }, 300);
  };

  const formatCPF = (cpf: string): string => {
    const cleaned = cpf.replace(/\D/g, "");
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return cpf;
  };

  const handleChange = (option: UserOption | null) => {
    setSelectedOption(option);
    const cleanDocument = option?.document.replace(/\D/g, "") || "";
    onChange(cleanDocument);
  };

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      fontFamily: "'Lexend Deca', sans-serif",
      fontSize: "0.95rem",
      minHeight: "40px",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#2684FF"
        : state.isFocused
        ? "#E6F0FF"
        : "white",
      color: state.isSelected ? "white" : "black",
      fontFamily: "'Lexend Deca', sans-serif",
      fontSize: "0.95rem",
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: "300px",
    }),
  };

  const MenuList = (props: any) => {
    const menuListRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const menuElement = menuListRef.current;
      if (!menuElement) return;

      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = menuElement;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 5;
        
        scrollPositionRef.current = scrollTop;
        
        if (isNearBottom && hasMore && !isLoadingMore.current) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchUsers(nextPage, searchTerm);
        }
      };

      menuElement.addEventListener('scroll', handleScroll);
      return () => menuElement.removeEventListener('scroll', handleScroll);
    }, [hasMore, page, searchTerm]);

    useEffect(() => {
      const menuElement = menuListRef.current;
      if (menuElement && scrollPositionRef.current > 0) {
        requestAnimationFrame(() => {
          menuElement.scrollTop = scrollPositionRef.current;
        });
      }
    }, [users.length]);

    return (
      <components.MenuList {...props} innerRef={menuListRef}>
        {props.children}
        {hasMore && (
          <div
            style={{
              padding: "8px 12px",
              textAlign: "center",
              fontFamily: "'Lexend Deca', sans-serif",
              fontSize: "0.85rem",
              color: "#999",
              backgroundColor: "#f8f9fa",
            }}
          >
            {isLoadingMore.current ? "Carregando mais..." : "Role para carregar mais"}
          </div>
        )}
      </components.MenuList>
    );
  };

  const NoOptionsMessage = (props: any) => {
    return (
      <div
        {...props.innerProps}
        style={{
          padding: "8px 12px",
          fontFamily: "'Lexend Deca', sans-serif",
          fontSize: "0.95rem",
          color: "#666",
        }}
      >
        {isLoadingMore.current ? "Carregando mais..." : props.children}
      </div>
    );
  };

  if (!corbanId) {
    return (
      <FormControl isInvalid={!!error} isRequired={isRequired} isDisabled={isDisabled}>
        <FormLabel
          fontFamily="Lexend Deca"
          fontSize="0.95rem"
        >
          {label}
        </FormLabel>
        <Input
          type="tel"
          placeholder={isDisabled ? "Salve o Corban primeiro para definir um vendedor autorizado" : "000.000.000-00"}
          maxLength={14}
          value={value ? formatCPF(value) : ""}
          onChange={(e) => {
            let formattedValue = e.target.value;
            if (cpfFormatter) {
              formattedValue = cpfFormatter(formattedValue);
            }
            const cleanValue = formattedValue.replace(/\D/g, "");
            onChange(cleanValue);
          }}
          isDisabled={isDisabled}
          fontFamily="Lexend Deca"
          fontSize="0.95rem"
        />
        {error && (
          <FormErrorMessage fontFamily="Lexend Deca">
            {error}
          </FormErrorMessage>
        )}
      </FormControl>
    );
  }

  return (
    <FormControl isInvalid={!!error} isRequired={isRequired} isDisabled={isDisabled}>
      <FormLabel
        fontFamily="Lexend Deca"
        fontSize="0.95rem"
      >
        {label}
      </FormLabel>
      <Select
        options={users}
        onChange={handleChange}
        value={selectedOption}
        onInputChange={handleInputChange}
        placeholder={placeholder}
        noOptionsMessage={() => "Nenhum usuário encontrado"}
        loadingMessage={() => 
          isLoadingMore.current 
            ? "Carregando mais resultados..." 
            : "Carregando..."
        }
        styles={customStyles}
        isLoading={isLoading}
        components={{ MenuList, NoOptionsMessage }}
        filterOption={null}
        isDisabled={isDisabled}
        isClearable
      />
      {error && (
        <FormErrorMessage fontFamily="Lexend Deca">
          {error}
        </FormErrorMessage>
      )}
    </FormControl>
  );
}

